/* Wonderbooths alternatives — tiny shared behaviours (no dependencies) */
(() => {
  if (window.__wb) return;
  window.__wb = true;

  // Analytics: same two destinations as the builder (frontend/utils.ts)
  const track = (name, params = {}) => {
    if (window.clarity) window.clarity('event', name);
    if (window.gtag) window.gtag('event', name, params);
  };

  // Product page measurements shared by gallery() and insight() (2026-10-24)
  const t0 = performance.now();
  const secOnPage = () => Math.round((performance.now() - t0) / 100) / 10;
  const fromAd = (() => { const q = new URLSearchParams(location.search); return q.has('fbclid') || /^(facebook|fb|ig|instagram|meta)$/i.test(q.get('utm_source') || ''); })();
  const page = { maxGallery: 1 };

  // Reveal on scroll
  const io = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
      }), { rootMargin: '0px 0px -8% 0px' })
    : null;
  const scan = (root = document) => root.querySelectorAll('.wb-rv:not(.is-in)').forEach((el) => io ? io.observe(el) : el.classList.add('is-in'));

  // Sticky CTA: show once the element in data-wb-sticky-after has scrolled out of view
  const sticky = () => document.querySelectorAll('.wb-sticky').forEach((bar) => {
    // Product page: one button at a time. The bar steps aside while the page's own button for the same action is
    // on screen: the upload card before a preview (data-hide-pre), Add to cart after it (data-hide-post).
    if (bar.dataset.hidePre || bar.dataset.hidePost) {
      const sec = bar.closest('.wb-pdp-sec') || document;
      const pdp = sec.querySelector('.wb-pdp');
      const seen = new Map();
      // (2026-11-02) the first time the bar shows "Add photo", the button glows softly 3 times so nobody misses it, then
      // stays still; once per page view (wb.css: is-hint, off with reduced motion)
      let hinted = false;
      const update = () => {
        const post = pdp && pdp.classList.contains('has-generated');
        const sel = post ? bar.dataset.hidePost : bar.dataset.hidePre;
        const el = sel && sec.querySelector(sel);
        bar.classList.toggle('is-on', !(el && seen.get(el)));
        if (!hinted && !post && bar.classList.contains('is-on')) {
          hinted = true;
          setTimeout(() => { bar.classList.add('is-hint'); setTimeout(() => bar.classList.remove('is-hint'), 7000); }, 600);
        }
      };
      // the bottom 96px is where the bar itself sits: a button under it doesn't count as on screen
      const io = new IntersectionObserver((es) => { es.forEach((e) => seen.set(e.target, e.isIntersecting)); update(); }, { rootMargin: '-40px 0px -96px 0px' });
      [bar.dataset.hidePre, bar.dataset.hidePost].forEach((sel) => { const el = sel && sec.querySelector(sel); if (el) io.observe(el); });
      if (pdp) new MutationObserver(update).observe(pdp, { attributes: true, attributeFilter: ['class'] });
      update();
      return;
    }
    if (bar.dataset.after === 'none') { bar.classList.add('is-on'); return; }
    const target = document.querySelector(bar.dataset.after || '.wb-hero');
    if (!target) { bar.classList.add('is-on'); return; }
    new IntersectionObserver(([e]) => bar.classList.toggle('is-on', !e.isIntersecting && e.boundingClientRect.top < 0)).observe(target);
  });

  // Before/after slider
  const ba = () => document.querySelectorAll('[data-wb-ba]').forEach((el) => {
    const set = (x) => {
      const r = el.getBoundingClientRect();
      const p = Math.min(100, Math.max(0, ((x - r.left) / r.width) * 100));
      el.style.setProperty('--ba', p + '%');
      el.classList.add('is-touched');
    };
    let down = false;
    el.addEventListener('pointerdown', (e) => { down = true; el.setPointerCapture(e.pointerId); set(e.clientX); });
    el.addEventListener('pointermove', (e) => down && set(e.clientX));
    el.addEventListener('pointerup', () => { down = false; });
  });

  // Magic loop: rotate the featured product on every iteration
  // (2026-11-01) its pictures load only when it comes near the screen, then one product ahead: before, all of them
  // (~850 KB) loaded with the page even far below the first screen
  const loops = () => document.querySelectorAll('[data-wb-loop]').forEach((el) => {
    let sets = [];
    try { sets = JSON.parse(el.dataset.wbLoop); } catch (e) { sets = []; }
    const preload = (s) => s && [s.photo, s.art, s.room].forEach((u) => { const im = new Image(); im.src = u; });
    let near = false;
    const show = () => {
      if (near) return;
      near = true;
      if (el.dataset.wbBg) el.style.cssText += ';' + el.dataset.wbBg;
      preload(sets[1]);
    };
    if ('IntersectionObserver' in window) {
      const o = new IntersectionObserver((es) => { if (es.some((e) => e.isIntersecting)) { o.disconnect(); show(); } }, { rootMargin: '800px 0px' });
      o.observe(el);
    } else show();
    if (sets.length < 2) return;
    let i = 0;
    const anim = el.querySelector('.wb-loop__clock');
    if (!anim) return;
    anim.addEventListener('animationiteration', () => {
      if (!near) return;
      i = (i + 1) % sets.length;
      const s = sets[i];
      preload(sets[(i + 1) % sets.length]);
      el.style.setProperty('--wb-photo', `url("${s.photo}")`);
      el.style.setProperty('--wb-art', `url("${s.art}")`);
      el.style.setProperty('--wb-room', `url("${s.room}")`);
      el.querySelectorAll('[data-wb-loop-title]').forEach((t) => { t.textContent = s.title; });
    });
  });

  // Delivery date ("Order today, get it by …")
  const dates = () => document.querySelectorAll('[data-wb-arrive]').forEach((el) => {
    const [min, max] = (el.dataset.wbArrive || '5,8').split(',').map(Number);
    const add = (n) => { const d = new Date(); let k = 0; while (k < n) { d.setDate(d.getDate() + 1); if (d.getDay() % 6) k++; } return d; };
    const f = (d) => d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    // data-wb-arrive-by: one date, the latest ("arrives by Thu, Nov 5"); otherwise the range
    el.textContent = el.hasAttribute('data-wb-arrive-by') ? f(add(max)) : `${f(add(min))} – ${f(add(max))}`;
  });

  // Short sizes line before a preview (wb-pdp sizes_short): tap shows / hides the full size list
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-wb-sizes-toggle]');
    if (!b) return;
    const box = b.closest('.wb-pdp')?.querySelector('[data-wb-sizes]');
    if (!box) return;
    const open = !box.classList.contains('is-open');
    box.classList.toggle('is-open', open);
    b.setAttribute('aria-expanded', String(open));
    if (open) track('sizes_summary_opened', {});
  });

  // Open the portrait builder from any [data-wb-open]
  document.addEventListener('click', (e) => {
    const t = e.target.closest('[data-wb-open]');
    if (!t) return;
    e.preventDefault();
    track('builder_open_clicked', { from: t.closest('.wb-sticky') ? 'sticky_bar' : (t.closest('section')?.className.match(/wb-[a-z]+/) || ['page'])[0] });
    window.__wbOpenFrom = { where: t.closest('.wb-sticky') ? 'sticky_bar' : t.closest('.wb-hero') ? 'hero_button' : 'other', at: Date.now() };
    if (typeof window.openImageUploadModal === 'function') window.openImageUploadModal();
    else document.querySelector('#generate-button-top button, .generate-button')?.click();
  });

  // Size picker: sets the variant id on the product form and syncs prices
  const sizes = () => document.querySelectorAll('[data-wb-sizes]').forEach((group) => {
    const form = document.getElementById(group.dataset.form);
    const input = form?.querySelector('input[name="id"]');
    const flow = group.closest('.wb-pdp--sizeflow');
    const pdp = group.closest('.wb-pdp');
    const msg = group.querySelector('[data-wb-size-msg]');
    const digNote = group.querySelector('[data-wb-dig-note]');
    // TESTING ONLY: ?wbdigital=1 shows a sample "Digital file" row until the real variant exists (see wb-pdp.liquid)
    const fake = group.querySelector('template[data-wb-fake-digital]');
    if (fake && new URLSearchParams(location.search).has('wbdigital')) group.querySelector('.wb-sizes__list')?.append(fake.content.cloneNode(true));
    let previewAt = 0;
    group.addEventListener('change', (e) => {
      const r = e.target.closest('input[type="radio"]');
      if (!r || !input) return;
      if (!r.hasAttribute('data-fake')) input.value = r.value;
      const by = e.isTrusted ? 'customer' : 'auto';
      track('size_selected', { size: r.dataset.label || '', by, has_preview: !!pdp?.classList.contains('has-generated') });
      if (window.clarity && by === 'customer') window.clarity('set', 'size_chosen', 'yes');
      flow?.classList.add('is-sized');
      document.querySelectorAll('[data-wb-price]').forEach((p) => { p.textContent = r.dataset.price; });
      document.querySelectorAll('[data-wb-compare]').forEach((p) => { p.textContent = r.dataset.compare || ''; });
      document.querySelectorAll('[data-wb-size-label]').forEach((p) => { p.textContent = r.dataset.label; });
      document.querySelectorAll('[data-wb-viz]').forEach((z) => { z.style.setProperty('--w', r.dataset.w); z.style.setProperty('--h', r.dataset.h); });
      // what is selected, in words: the size we picked for them, or the one they tapped
      if (msg) msg.textContent = by === 'auto' && r.hasAttribute('data-popular')
        ? '✓ Our most popular size, picked for you. Tap to change.'
        : `✓ ${r.dataset.name || r.dataset.label} selected`;
      if (digNote) digNote.hidden = !r.hasAttribute('data-digital');
      flow?.classList.toggle('is-digital', r.hasAttribute('data-digital'));
      // sticky bar: the chosen size instead of the product name ("16″ × 20″ canvas")
      if (flow) document.querySelectorAll('[data-wb-sticky-name]').forEach((el) => {
        // the sale version of the bar (wb-sticky__sz) has the struck price next to it: the size alone fits small phones
        el.textContent = r.hasAttribute('data-digital') ? 'Digital file' : el.classList.contains('wb-sticky__sz') ? (r.dataset.name || r.dataset.label) : `${r.dataset.name || r.dataset.label} canvas`;
      });
      if (!r.hasAttribute('data-fake')) { const url = new URL(location.href); url.searchParams.set('variant', r.value); history.replaceState(null, '', url); }
    });
    if (!flow || !pdp) return;
    // Size after preview: once they have their preview on the page (the builder sends wb:preview-shown when the drawer
    // closes, or a saved preview is restored), Most popular is picked for them, so the next step is simply "Add to cart". Then one gentle pulse on that card the first time the sizes come into view.
    const pickPopular = () => {
      if (flow.classList.contains('is-sized') || !pdp.classList.contains('has-generated')) return;
      previewAt = Date.now();
      const pick = group.querySelector('input[data-popular]:not(:disabled)') || group.querySelector('input:not(:disabled):not([data-digital])');
      if (!pick) return;
      pick.checked = true;
      pick.dispatchEvent(new Event('change', { bubbles: true }));
      if (!('IntersectionObserver' in window)) return;
      const seen = new IntersectionObserver(([en]) => {
        if (!en.isIntersecting) return;
        seen.disconnect();
        track('sizes_seen', { has_preview: true, sec_since_preview: Math.round((Date.now() - previewAt) / 1000) });
        document.dispatchEvent(new CustomEvent('wb:sizes-seen'));
        if (window.clarity) window.clarity('set', 'sizes_seen', 'yes');
        const card = group.querySelector('input:checked')?.closest('.wb-size');
        if (!card) return;
        card.classList.add('is-pulse');
        setTimeout(() => card.classList.remove('is-pulse'), 700);
      }, { threshold: 0.5 });
      seen.observe(group);
    };
    document.addEventListener('wb:preview-shown', () => setTimeout(pickPopular, 0));
  });

  // Size after preview: until a size is tapped, "Choose your size" scrolls to the sizes and picks Most popular.
  // Capture phase, so it runs before product-form-intercept.js (the sticky bar clicks the same button).
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.wb-pdp__atc');
    const sec = btn?.closest('.wb-pdp--sizeflow');
    if (!sec || sec.classList.contains('is-sized') || !sec.querySelector('.wb-pdp.has-generated')) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    track('size_cta_tapped', { where: e.isTrusted ? 'inline' : 'sticky' });
    const group = sec.querySelector('[data-wb-sizes]');
    const pick = group?.querySelector('input[data-popular]:not(:disabled)') || group?.querySelector('input:not(:disabled)');
    if (!group || !pick) return;
    group.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setTimeout(() => {
      pick.checked = true;
      pick.dispatchEvent(new Event('change', { bubbles: true }));
      group.classList.add('is-asking');
      setTimeout(() => group.classList.remove('is-asking'), 1500);
    }, 450);
  }, true);

  // Mirror builder state: once previews exist, flag the page
  const builder = () => {
    const pdp = document.querySelector('.wb-pdp');
    if (!pdp) return;
    new MutationObserver(() => {
      const on = pdp.classList.contains('has-generated');
      document.documentElement.classList.toggle('wb-has-preview', on);
    }).observe(pdp, { attributes: true, attributeFilter: ['class'] });
  };

  // v2 builder: mockups live next to the builder on phones, in the media column on desktop
  // desktop: the portrait box sits in the left column (before the photos); phone: back in its place under the price.
  // Follows the window width (2026-10-27: a page opened wide and then made narrow kept the portrait above the title).
  const genbox = () => {
    const box = document.querySelector('.wb-pdp__gen--box');
    const media = document.querySelector('.wb-pdp .wb-pdp__media');
    if (!box || !media) return;
    const home = document.createComment('wb-genbox');
    box.parentElement.insertBefore(home, box);
    const mq = window.matchMedia('(min-width: 990px)');
    const place = () => { if (mq.matches) media.parentElement.insertBefore(box, media); else home.parentElement.insertBefore(box, home.nextSibling); };
    place();
    mq.addEventListener('change', place);
  };

  // Seasonal campaign: hide after cut-off (cached pages), show "N days left"
  // Sale end (data-wb-ends, an ISO time with zone): "2 days 5 h left", then hours and minutes, gone at the end.
  const saleEnds = () => document.querySelectorAll('[data-wb-ends]').forEach((el) => {
    const end = new Date(el.dataset.wbEnds);
    if (isNaN(end)) return;
    const c = el.querySelector('[data-wb-countdown]');
    const tick = () => {
      const ms = end - Date.now();
      if (ms <= 0) { el.remove(); return; }
      const d = Math.floor(ms / 86400000), h = Math.floor((ms % 86400000) / 3600000), m = Math.floor((ms % 3600000) / 60000);
      // short (one-row banner): "10 days left", "1 day 5 h left", "23 h left", "45 min left" (fits one row on any phone)
      if (c && c.dataset.wbCountdown === 'short') { c.textContent = d >= 2 ? `${d} days left` : d === 1 ? `1 day ${h} h left` : h >= 1 ? `${h} h left` : `${m} min left`; setTimeout(tick, 30000); return; }
      if (c) c.textContent = d >= 1 ? `${d} day${d > 1 ? 's' : ''} ${h} h left` : h >= 1 ? `${h} h ${m} min left` : `${m} min left`;
      setTimeout(tick, 30000);
    };
    tick();
  });

  const season = () => document.querySelectorAll('[data-wb-cutoff]').forEach((el) => {
    const end = new Date(el.dataset.wbCutoff + 'T23:59:59');
    const days = Math.ceil((end - new Date()) / 86400000);
    if (isNaN(days)) return;
    if (days < 0) { el.remove(); return; }
    const c = el.querySelector('[data-wb-countdown]');
    if (c) c.textContent = days === 0 ? 'Last day!' : days === 1 ? '1 day left' : days <= 14 ? days + ' days left' : '';
  });

  // Hero fan: the cards take turns in front (data-pos 2 = front, 1 / 3 = behind, off = hidden).
  // With a reaction video (first card): it plays alone, large (.is-video hides the portraits), once to the end.
  // Then it leaves for good and the portraits take turns. If it can't play, the portraits start right away.
  const dance = () => document.querySelectorAll('[data-wb-dance]').forEach((fan) => {
    const all = [...fan.querySelectorAll('.wb-fan__c')];
    const vidCard = all.find((c) => c.classList.contains('wb-fan__c--vid'));
    const vid = vidCard && vidCard.querySelector('video');
    const cards = all.filter((c) => c !== vidCard);
    const still = cards.length < 3 || window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const n = cards.length;
    let front = 1;
    const place = () => cards.forEach((c, i) => { const r = (i - front + n) % n; c.dataset.pos = r === 0 ? '2' : r === 1 ? '3' : r === n - 1 ? '1' : 'off'; });
    const turns = () => {
      if (vidCard) { vidCard.dataset.pos = 'off'; fan.classList.remove('is-video'); }
      place();
      if (!still) setInterval(() => { if (document.hidden) return; front = (front + 1) % n; place(); }, 2800);
    };
    if (!vid) return turns();
    let done = false;
    const next = () => { if (done) return; done = true; clearTimeout(giveUp); turns(); };
    const giveUp = setTimeout(next, 20000);
    vid.loop = false;
    vid.addEventListener('ended', next);
    vid.addEventListener('error', next);
    if (vid.ended) next(); else vid.play().catch(next);
  });

  // Product gallery: dots follow the photo in view, dots and ‹ › arrows jump to a photo.
  // One order for every visitor (2026-10-24, replaces the ads-only swap): Shopify image 2 ("Your photo → Your portrait")
  // first, then the "kid-holding" photo if there is one, then image 1 (the clean hero), then the rest.
  // Collection cards keep image 1.
  const gallery = () => document.querySelectorAll('.wb-gal').forEach((g) => {
    const strip = g.querySelector('.wb-gal__track');
    if (!strip) return;
    const src = (el) => { const i = el.querySelector('img'); return i ? (i.getAttribute('src') || '') : ''; };
    // wb-pdp already renders this order (2026-11-01, data-wb-ordered), so the first photo is the one that loads first
    if (g.querySelector('.wb-gal__nav') && strip.children.length >= 2 && !('wbOrdered' in strip.dataset)) {
      const [hero, second, ...rest] = [...strip.children];
      const kid = rest.find((el) => /kid-holding/i.test(src(el)));
      [second, ...(kid ? [kid] : []), hero, ...rest.filter((el) => el !== kid)].forEach((el) => strip.appendChild(el));
      // the browser keeps the old first photo in view after the move: go back to the start
      strip.scrollLeft = 0;
      requestAnimationFrame(() => { strip.scrollLeft = 0; });
    }
    const slides = [...strip.children];
    // dots, or thumbnails on builder pages (wb-gal--thumbs, 2026-11-01)
    const dots = [...g.querySelectorAll('.wb-gal__dots button, .wb-gal__thumbs button:not(.wb-gal__nav)')];
    const current = () => {
      const x = strip.scrollLeft;
      let best = 0;
      slides.forEach((sl, i) => { if (Math.abs(sl.offsetLeft - slides[0].offsetLeft - x) < Math.abs(slides[best].offsetLeft - slides[0].offsetLeft - x)) best = i; });
      return best;
    };
    // ‹ on the first photo goes to the last, › on the last to the first: an arrow never does nothing (2026-10-21)
    const go = (i) => {
      const k = ((i % slides.length) + slides.length) % slides.length;
      strip.scrollTo({ left: slides[k].offsetLeft - slides[0].offsetLeft, behavior: 'smooth' });
    };
    // the thumbnail row scrolls along so the framed thumbnail is always in view
    const row = g.querySelector('.wb-gal__strip');
    let shown = 0;
    const follow = (c) => {
      if (!row || c === shown || !dots[c]) return;
      shown = c;
      const d = dots[c];
      const left = d.offsetLeft - row.offsetLeft, right = left + d.offsetWidth;
      if (left < row.scrollLeft) row.scrollTo({ left: left - 4, behavior: 'smooth' });
      else if (right > row.scrollLeft + row.clientWidth) row.scrollTo({ left: right - row.clientWidth + 4, behavior: 'smooth' });
    };
    let raf = 0;
    strip.addEventListener('scroll', () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => { const c = current(); if (c) g.classList.add('is-moved'); /* another photo is shown: the glowing › (wb-gal--thumbs) has done its job */ dots.forEach((d, i) => { d.classList.toggle('is-on', i === c); if (i === c) d.setAttribute('aria-current', 'true'); else d.removeAttribute('aria-current'); }); follow(c); });
    }, { passive: true });
    // which control people use (2026-11-01, thumbnails + arrows on phones): gallery_thumb_tapped / gallery_arrow_tapped
    const onPdp = !!g.closest('.wb-pdp');
    dots.forEach((d, i) => d.addEventListener('click', () => {
      if (onPdp && d.closest('.wb-gal__thumbs')) track('gallery_thumb_tapped', { index: i + 1, from: current() + 1 });
      go(i);
    }));
    const arrow = (dir) => () => {
      const to = ((current() + dir) % slides.length + slides.length) % slides.length;
      if (onPdp) track('gallery_arrow_tapped', { direction: dir > 0 ? 'next' : 'back', index: to + 1 });
      go(current() + dir);
    };
    g.querySelector('.wb-gal__nav--prev')?.addEventListener('click', arrow(-1));
    g.querySelector('.wb-gal__nav--next')?.addEventListener('click', arrow(1));

    // gallery_slide_seen: a photo at least 60 % in view for 0.7 s after the shopper moved the gallery (swipe, arrow,
    // dot), once per photo. Photo 1 also counts once after 2 s on screen. Code-driven scrolling never counts.
    if (!g.closest('.wb-pdp') || !('IntersectionObserver' in window)) return;
    const name = (el) => (src(el).split('?')[0].split('/').pop() || '').replace(/\.[a-z0-9]+$/i, '').replace(/_[0-9a-f]{8}-[0-9a-f-]{27,}$/i, '');
    const hasPreview = () => !!document.querySelector('.wb-pdp.has-generated');
    let moved = false;
    const sent = new Set();
    const send = (i) => {
      if (sent.has(i)) return;
      sent.add(i);
      page.maxGallery = Math.max(page.maxGallery, i + 1);
      track('gallery_slide_seen', { index: i + 1, total: slides.length, image: name(slides[i]), from_ad: fromAd, has_preview: hasPreview(), sec_on_page: secOnPage() });
    };
    const userMove = () => { moved = true; };
    strip.addEventListener('touchstart', userMove, { passive: true });
    strip.addEventListener('pointerdown', userMove, { passive: true });
    strip.addEventListener('wheel', (e) => { if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) userMove(); }, { passive: true });
    strip.addEventListener('keydown', userMove);
    g.querySelectorAll('.wb-gal__nav, .wb-gal__dots button, .wb-gal__thumbs button:not(.wb-gal__nav)').forEach((b) => b.addEventListener('click', userMove));
    const timers = new Map();
    const io = new IntersectionObserver((es) => es.forEach((en) => {
      const i = slides.indexOf(en.target);
      if (en.intersectionRatio >= 0.6) {
        if (timers.has(i)) return;
        timers.set(i, setTimeout(() => { timers.delete(i); if (moved || i === 0) send(i); }, i === 0 && !moved ? 2000 : 700));
      } else if (timers.has(i)) { clearTimeout(timers.get(i)); timers.delete(i); }
    }), { root: strip, threshold: [0, 0.6] });
    slides.forEach((sl) => io.observe(sl));
  });

  // Reaction clips cut from one video. data-wb-parts="0.1-4.1, 7.8-9.2": the circle plays only those scenes, in turn,
  // so circles cut from the same file never show the same moment. Plain clips jump back to their start on every loop.
  const clips = () => {
    const vids = [...document.querySelectorAll('video[data-wb-start]')];
    vids.forEach((v) => {
      const start = parseFloat(v.dataset.wbStart) || 0;
      const parts = (v.dataset.wbParts || '').split(',').map((p) => p.split('-').map(parseFloat)).filter(([a, b]) => b > a);
      let n = 0;
      let last = 0;
      const check = () => {
        const t = v.currentTime;
        if (parts.length) {
          const [a, b] = parts[n];
          if (t >= b) { n = (n + 1) % parts.length; v.currentTime = parts[n][0]; } else if (t < a - 0.2) v.currentTime = a;
        } else if (t + 0.5 < last && t < start) v.currentTime = start;
        last = v.currentTime;
      };
      v.addEventListener('timeupdate', check);
      // some browsers ignore #t= or refuse to autoplay: every circle would then sit on the file's first frame
      const first = parts.length ? parts[0][0] : start;
      const seek = () => { if (v.currentTime < first - 0.2) v.currentTime = first; };
      v.addEventListener('loadedmetadata', seek);
      v.addEventListener('loadeddata', seek);
      // timeupdate fires ~4×/s, too late for a clean cut; frame callbacks catch the end of a scene exactly where supported
      if (parts.length && v.requestVideoFrameCallback) {
        const frame = () => { check(); v.requestVideoFrameCallback(frame); };
        v.requestVideoFrameCallback(frame);
      }
    });
    // data-wb-src: nothing downloads until the row is about a screen away. Each circle loads its file directly:
    // iPhone Safari stays black on a shared blob: copy (tried), and the circle files are small (≈400 KB).
    const play = (v) => { v.preload = 'auto'; v.src = v.dataset.wbSrc; v.play().catch(() => {}); };
    const start = (vs) => vs.forEach(play);
    const rows = new Map();
    vids.filter((v) => v.dataset.wbSrc).forEach((v) => {
      const row = v.closest('section') || v.parentElement;
      rows.set(row, [...(rows.get(row) || []), v]);
    });
    if (!('IntersectionObserver' in window)) return rows.forEach(start);
    const io = new IntersectionObserver((entries) => entries.forEach((e) => {
      if (!e.isIntersecting) return;
      io.unobserve(e.target);
      start(rows.get(e.target));
    }), { rootMargin: '800px 0px' });
    rows.forEach((_, row) => io.observe(row));
  };

  // Product page analytics (2026-10-15, BRIEF_AFTER_PREVIEW.md part 2): where people look and where they stop.
  // Never sends photos, preview ids or names. GA + Clarity through track(); Clarity tags make recordings filterable.
  const insight = () => {
    const pdp = document.querySelector('.wb-pdp');
    if (!pdp) return;
    const q = new URLSearchParams(location.search);
    const hasPreview = () => pdp.classList.contains('has-generated');
    if (window.clarity) {
      const handle = (location.pathname.match(/\/products\/([^/?#]+)/) || [])[1];
      if (handle) window.clarity('set', 'product', handle);
      if (q.get('utm_content')) window.clarity('set', 'ad', q.get('utm_content').slice(0, 60));
    }
    let previewAt = 0;
    let lastSection = '';
    let sizesSeen = false;
    let sizeChosen = false;
    document.addEventListener('wb:preview-shown', () => { previewAt = previewAt || Date.now(); });
    document.addEventListener('change', (e) => { if (e.isTrusted && e.target.closest?.('[data-wb-sizes]')) sizeChosen = true; });
    // scroll depth: 25 / 50 / 75 / 100 %, once each
    const marks = [25, 50, 75, 100];
    const onScroll = () => {
      const pct = ((scrollY + innerHeight) / document.documentElement.scrollHeight) * 100;
      while (marks.length && pct >= marks[0] - 1) track('scroll_depth', { percent: marks.shift(), has_preview: hasPreview() });
      if (!marks.length) removeEventListener('scroll', onScroll);
    };
    addEventListener('scroll', onScroll, { passive: true });
    // sections: at least half on screen for a second, once each
    if ('IntersectionObserver' in window) {
      const named = [
        ['gallery', pdp.querySelector('.wb-gal')], ['price_top', pdp.querySelector('.wb-pdp__price')],
        ['room_previews', document.getElementById('generated-media-placeholder')], ['sizes', pdp.querySelector('[data-wb-sizes]')],
        ['shipping', pdp.querySelector('.wb-pdp__deadline')],
      ];
      document.querySelectorAll('.shopify-section').forEach((el) => {
        const key = (el.id.split('__').pop() || '').replace(/[-_]\d+$/, '').replace(/-/g, '_');
        if (key && key !== 'main' && !/header|footer|announcement/.test(el.id)) named.push([key, el]);
      });
      const timers = new Map();
      const seen = new Set();
      const io = new IntersectionObserver((es) => es.forEach((en) => {
        const name = en.target.dataset.wbSection;
        if (en.isIntersecting) {
          lastSection = name;
          if (seen.has(name) || timers.has(name)) return;
          timers.set(name, setTimeout(() => {
            if (name === 'room_previews' && !hasPreview()) { timers.delete(name); return; }
            seen.add(name);
            track('section_seen', { section: name, has_preview: hasPreview() });
          }, 1000));
        } else if (timers.has(name) && !seen.has(name)) { clearTimeout(timers.get(name)); timers.delete(name); }
      }), { threshold: 0.5 });
      named.forEach(([name, el]) => { if (!el) return; el.dataset.wbSection = name; io.observe(el); });
    }
    document.addEventListener('wb:sizes-seen', () => { sizesSeen = true; });
    // the sticky bar: which button they tap
    document.addEventListener('click', (e) => {
      const b = e.target.closest('.wb-sticky button');
      if (!b) return;
      const sized = !!b.closest('.wb-pdp--sizeflow.is-sized') || !b.closest('.wb-pdp--sizeflow');
      track('sticky_cta_tapped', { label: b.classList.contains('wb-sticky__pre') ? 'upload' : sized ? 'add_to_cart' : 'choose_size', has_preview: hasPreview() });
    });
    // FAQ: which questions people open
    document.addEventListener('toggle', (e) => {
      const d = e.target;
      if (!(d instanceof HTMLDetailsElement) || !d.open || !d.closest('.shopify-section[id*="faq"]')) return;
      const question = (d.querySelector('summary')?.textContent || '').toLowerCase().replace(/[^a-z0-9 ]/g, '').trim().split(/\s+/).slice(0, 6).join('_');
      track('faq_opened', { question, has_preview: hasPreview() });
    }, true);
    // first_action: the shopper's first real interaction on the product page, once
    let firstDone = false;
    const first = (kind) => {
      if (firstDone) return;
      firstDone = true;
      track('first_action', { kind, sec_on_page: secOnPage() });
      ['click', 'touchmove', 'wheel', 'keydown'].forEach((t) => document.removeEventListener(t, onFirst, true));
    };
    const onFirst = (e) => {
      if (!e.isTrusted) return;
      const el = e.target instanceof Element ? e.target : null;
      const inGal = !!el?.closest('.wb-gal');
      if (e.type === 'touchmove') return first(inGal ? 'gallery_swipe' : 'scroll');
      if (e.type === 'wheel') return first(inGal && Math.abs(e.deltaX) > Math.abs(e.deltaY) ? 'gallery_swipe' : 'scroll');
      if (e.type === 'keydown') return ['ArrowDown', 'ArrowUp', 'PageDown', 'PageUp', ' ', 'End', 'Home'].includes(e.key) ? first('scroll') : undefined;
      if (el?.closest('[data-wb-sizes]')) return first('size_tap');
      if (el?.closest('#wb-builder, [data-wb-open], .wb-sticky__pre')) return first('upload_tap');
      if (inGal) return first('gallery_swipe');
      first('other');
    };
    ['click', 'touchmove', 'wheel', 'keydown'].forEach((t) => document.addEventListener(t, onFirst, { capture: true, passive: true }));
    // reviews_interaction: the stars or the quote under the title, or a tap into the Loox reviews widget
    const revSent = new Set();
    const rev = (where) => { if (revSent.has(where)) return; revSent.add(where); track('reviews_interaction', { where, has_preview: hasPreview() }); };
    document.addEventListener('click', (e) => {
      const a = e.target.closest?.('.wb-pdp__rating, .wb-pdp__mini, .wb-pdp__proof, a[href="#wb-reviews"]');
      if (a) rev(a.classList.contains('wb-pdp__rating') ? 'title_stars' : a.classList.contains('wb-pdp__mini') ? 'title_quote' : a.classList.contains('wb-pdp__rl') ? 'title_rating_line' : a.classList.contains('wb-pdp__proof') ? 'title_proof' : 'link');
    });
    addEventListener('blur', () => setTimeout(() => { if (document.activeElement?.id === 'looxReviewsFrame') rev('loox_widget'); }, 0));
    // page_exit: every product page view, once, when the page is hidden or left (beacon, so it still goes out)
    let maxScroll = 0;
    addEventListener('scroll', () => { maxScroll = Math.max(maxScroll, Math.round(((scrollY + innerHeight) / document.documentElement.scrollHeight) * 100)); }, { passive: true });
    let activeMs = 0;
    let shownAt = document.visibilityState === 'visible' ? performance.now() : 0;
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') shownAt = performance.now();
      else if (shownAt) { activeMs += performance.now() - shownAt; shownAt = 0; }
    });
    let exited = false;
    const exit = () => {
      if (exited || document.visibilityState !== 'hidden') return;
      exited = true;
      if (shownAt) { activeMs += performance.now() - shownAt; shownAt = 0; }
      if (window.clarity) window.clarity('set', 'gallery_max', String(page.maxGallery));
      track('page_exit', { last_section: lastSection, max_gallery_index: page.maxGallery, max_scroll_pct: Math.min(100, maxScroll), active_sec: Math.round(activeMs / 1000), has_preview: hasPreview(), from_ad: fromAd, transport_type: 'beacon' });
    };
    document.addEventListener('visibilitychange', exit);
    addEventListener('pagehide', exit);
    // leaving after a preview, once: where they were and what they had done (beacon, so it still goes out)
    let left = false;
    const leave = () => {
      if (left || document.visibilityState !== 'hidden' || !hasPreview()) return;
      left = true;
      const p = { last_section: lastSection, sec_since_preview: previewAt ? Math.round((Date.now() - previewAt) / 1000) : 0, size_chosen: sizeChosen, sizes_seen: sizesSeen, version: (window.getSelectedVersion && window.getSelectedVersion()) || '', transport_type: 'beacon' };
      track('exit_after_preview', p);
    };
    document.addEventListener('visibilitychange', leave);
    addEventListener('pagehide', leave);
  };

  // Quotes under the product title taking turns, one every 5 s (still with "reduce motion"; paused while hidden)
  const rotate = () => document.querySelectorAll('[data-wb-rotate]').forEach((box) => {
    const items = [...box.children];
    if (items.length < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let i = 0;
    setInterval(() => {
      if (document.hidden) return;
      items[i].classList.remove('is-on');
      i = (i + 1) % items.length;
      items[i].classList.add('is-on');
    }, 5000);
  });

  // Theme dev only (localhost:9292): the dev store has no Loox, so the reviews spot gets the LIVE shop's Loox widget,
  // re-served by the Vite dev server (vite.config.js, /loox-mock), to see how it will look. ?wbloox=0 turns it off.
  const looxPreview = () => {
    if (location.port !== '9292' || new URLSearchParams(location.search).get('wbloox') === '0') return;
    document.querySelectorAll('#looxReviews').forEach((spot) => {
      if (spot.querySelector('iframe')) return;
      const product = ''; // all shop reviews: what product pages show once Loox combines all portraits' reviews
      const f = document.createElement('iframe');
      f.id = 'looxReviewsFrame';
      f.title = 'Reviews (localhost preview of the live Loox widget)';
      f.src = `http://${location.hostname}:3005/loox-mock/widget/OpY9rMpFHn/reviews${product}?limit=${spot.dataset.limit || 6}`;
      f.setAttribute('scrolling', 'no');
      f.style.cssText = 'width:100%;border:0;display:block;height:1340px';
      spot.appendChild(f);
    });
  };

  // Track everything (2026-11-03): every tap on anything tappable sends ui_click { area, label } (area = the page section,
  // label = our own button / link text, never anything a shopper typed), every sideways row sends row_swiped once, and the
  // important ones also get their own event. Clarity records all taps anyway; these make them countable in GA4.
  const everything = () => {
    if (window.__wbAll) return; window.__wbAll = true;
    const areaOf = (el) => {
      if (el.closest('header, .shopify-section-group-header-group, .header-wrapper')) return 'header';
      if (el.closest('footer, .shopify-section-group-footer-group')) return 'footer';
      if (el.closest('.wb-sticky')) return 'sticky_bar';
      if (el.closest('.b2-sheet, .b2')) return 'builder';
      if (el.closest('.cmg-zoom')) return 'main';
      const s = el.closest('.shopify-section');
      return s ? (s.id.split('__').pop() || 'page').replace(/[-_]\d+$/, '').replace(/-/g, '_') : 'page';
    };
    // saved photos / people in the builder can carry names the shopper typed: neutral label there
    const labelOf = (el) => el.closest('.b2-ft, .b2-cast, .b2-pick, .b2-names, [data-clarity-mask]') && !el.getAttribute('data-trk') ? '(photo or name)' : (el.getAttribute('data-trk') || el.getAttribute('aria-label') || (el.matches('input[type=radio], input[type=checkbox]') ? (el.closest('label')?.innerText || el.value) : el.innerText) || el.getAttribute('alt') || '')
      .replace(/\s+/g, ' ').trim().slice(0, 60);
    const hasPv = () => !!document.querySelector('.wb-pdp.has-generated');
    document.addEventListener('click', (e) => {
      const el = e.target.closest?.('a[href], button, summary, [role=button], input[type=radio], input[type=checkbox], label, .wb-cvs__ph, .wb-gal__s img, .cmg-stage img');
      if (!el || el.closest('[data-trk-skip]')) return;
      const area = areaOf(el), label = labelOf(el);
      track('ui_click', { area, label, has_preview: hasPv() });
      // named events for the important ones
      if (area === 'header') track('header_tapped', { what: el.closest('[href*="/cart"], .header__icon--cart') ? 'cart' : el.closest('.header__heading-link') ? 'logo' : el.closest('.header__icon--menu, summary') ? 'menu' : label });
      else if (area === 'footer' && el.matches('a')) track('footer_link_tapped', { label });
      const clip = el.closest('[data-wb-ugc]'); if (clip) track('reaction_video_opened', { index: [...clip.parentElement.children].indexOf(clip) + 1, caption: label });
      const card = el.closest('.wb-pc'); if (card) { const all = [...document.querySelectorAll('.wb-pc')]; track('related_product_tapped', { index: all.indexOf(card) + 1, product: ((card.matches('a') ? card : card.querySelector('a[href*="/products/"]'))?.getAttribute('href') || '').split('/products/')[1]?.split(/[?#]/)[0] || '', button: el.matches('a.wb-btn, button') }); }
      if (el.matches('[data-wb-quality-link]')) track('quality_tile_tapped', { label, to: el.getAttribute('href') });
      if (el.closest('.wb-faq') && el.matches('a')) track('help_message_tapped', {});
    }, true);
    document.addEventListener('submit', (e) => {
      const area = areaOf(e.target);
      if (e.target.querySelector('input[type=email]')) track('newsletter_signup', { area });
    }, true);
    // sideways rows (photo rows, reaction videos, product cards, thumbnails): once per row when they are swiped
    const swiped = new WeakSet();
    document.addEventListener('scroll', (e) => {
      const el = e.target; if (!(el instanceof Element) || swiped.has(el) || el.scrollWidth <= el.clientWidth + 4 || el.scrollLeft < 20) return;
      swiped.add(el); track('row_swiped', { area: areaOf(el), row: (el.className.baseVal ?? el.className).split(' ')[0] || el.tagName.toLowerCase() });
    }, true);
  };

  const init = () => { looxPreview(); rotate(); scan(); sticky(); ba(); loops(); dates(); sizes(); builder(); genbox(); season(); saleEnds(); dance(); gallery(); clips(); insight(); everything(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', (e) => { scan(e.target); });
})();
